<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAccount } from '../../../../composables/useAccount';
import BuyPlanModal from 'dashboard/components-next/BuyPlanModal.vue';
import usagesAPI from '../../../../api/usages';
import qrCodeImage from 'dashboard/assets/images/QR_code.jpg';

const { currentAccount } = useAccount();
const store = useStore();
const activeTab = ref('monthly');

// 购买弹窗状态
const buyPlanModalRef = ref(null);

// 联系销售二维码弹窗状态
const showQRModal = ref(false);
const selectedPlan = ref('');
const selectedBillingCycle = ref('monthly');

const customAttributes = computed(
  () => currentAccount.value?.custom_attributes || {}
);
const currentPlanName = computed(() => customAttributes.value.plan_name || '');
const currentBillingCycle = computed(() => customAttributes.value.plan_billing_cycle || '');
const planExpiresAt = computed(() => {
  const raw = customAttributes.value.subscription_ends_on;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
});

// 套餐状态
const planStatus = computed(() => {
  if (!currentPlanName.value) return { label: '未订阅', active: false };
  const raw = customAttributes.value.subscription_ends_on;
  if (raw && new Date(raw) < new Date()) return { label: '已过期', active: false };
  return { label: '生效中', active: true };
});

// 套餐计划名称（带颜色）
const planLabel = computed(() => {
  if (!currentPlanName.value) return '试用计划';
  if (currentPlanName.value === 'trial') return '试用计划';
  if (currentPlanName.value === 'enterprise') return '企业计划';
  return currentBillingCycle.value === 'yearly' ? '包年计划' : '包月计划';
});

const planSlogan = computed(() => {
  const map = {
    trial: '全功能体验，限时5天',
    standard: 'AI智能客服，轻松起步',
    professional: '高效团队协作，专业赋能',
    enterprise: '专属定制，无限可能',
  };
  return map[currentPlanName.value] || map.trial;
});

// 当前套餐价格
const currentPlanPrice = computed(() => {
  const priceMap = {
    trial: '免费',
    standard: currentBillingCycle.value === 'yearly' ? '¥1,026/年' : '¥95/月',
    professional: currentBillingCycle.value === 'yearly' ? '¥3,024/年' : '¥280/月',
    enterprise: '定制',
  };
  return priceMap[currentPlanName.value] || '';
});

// 用量数据
const accountLimits = computed(() => store.state.accounts?.limits || {});

// 坐席用量
const agentUsage = computed(() => {
  const allowed = accountLimits.value?.agents?.allowed || 0;
  const consumed = accountLimits.value?.agents?.consumed || 0;
  return { allowed, consumed, percent: allowed > 0 ? Math.round((consumed / allowed) * 100) : 0 };
});

// Token 用量（从 usages API 获取）
const tokenData = ref({ total: 0, used: 0 });
const fetchTokenUsage = async () => {
  try {
    const res = await usagesAPI.getUsage();
    const t = res.data?.pilot_tokens || {};
    tokenData.value = { total: t.total || 0, used: t.consumed || 0 };
  } catch {
    // 静默处理
  }
};
const tokenUsage = computed(() => {
  const { total, used } = tokenData.value;
  const balance = total - used;
  return { total, used, balance, percent: total > 0 ? Math.round((used / total) * 100) : 0 };
});

// 格式化数字
const formatNumber = num => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(num);
};

// 解析价格: "订阅 ¥95/月" → { symbol: '¥', amount: '95', unit: '/月' }
const parsePrice = priceStr => {
  if (!priceStr) return null;
  const cleaned = priceStr.replace('订阅 ', '');
  const match = cleaned.match(/^(¥)([\d,]+)(.*)$/);
  if (!match) return { full: cleaned };
  return { symbol: match[1], amount: match[2], unit: match[3] };
};

onMounted(() => {
  store.dispatch('accounts/limits');
  fetchTokenUsage();
});

const planTitleMap = {
  trial: '试用版',
  standard: '标准版',
  professional: '专业版',
  enterprise: '企业版',
};
const displayPlanName = computed(
  () => planTitleMap[currentPlanName.value] || currentPlanName.value || '未订阅'
);

// 当前套餐图标配置
const currentPlanConfig = computed(() => {
  const config = {
    trial: { icon: 'T', iconBg: 'from-green-500 to-green-600' },
    standard: { icon: 'S', iconBg: 'from-blue-500 to-blue-700', iconStyle: 'background: linear-gradient(135deg, #2563EB, #013068)' },
    professional: { icon: 'P', iconBg: 'from-blue-500 to-blue-700', iconStyle: 'background: linear-gradient(135deg, #2563EB, #013068)' },
    enterprise: { icon: 'E', iconBg: 'from-blue-500 to-blue-700', iconStyle: 'background: linear-gradient(135deg, #2563EB, #013068)' }
  };
  return config[currentPlanName.value] || { icon: '?', iconBg: 'from-gray-400 to-gray-500' };
});

// 购买限制逻辑
const getPurchaseStatus = planKey => {
  // 试用版：如果当前是标准版或专业版，不允许降级到试用版
  if (planKey === 'trial') {
    if (currentPlanName.value === 'standard' || currentPlanName.value === 'professional') {
      return { allowed: false, reason: '已升级付费版' };
    }
  }
  // trial 或无套餐 → 允许购买任何
  if (!currentPlanName.value || currentPlanName.value === 'trial') {
    return { allowed: true, reason: '' };
  }
  // 规则4：专业版不能买标准版
  if (currentPlanName.value === 'professional' && planKey === 'standard') {
    return { allowed: false, reason: '当前等级更高' };
  }
  // 规则3：升级（取消周期限制）
  // 移除原有的周期匹配检查，允许跨周期升级
  return { allowed: true, reason: '' };
};

const openBuyModal = planKey => {
  selectedPlan.value = planKey;
  selectedBillingCycle.value = activeTab.value;
  buyPlanModalRef.value?.open();
};

// 打开联系销售二维码弹窗
const openQRModal = () => {
  showQRModal.value = true;
};

// 关闭二维码弹窗
const closeQRModal = () => {
  showQRModal.value = false;
};

const plans = [
  {
    key: 'trial',
    icon: 'T',
    iconBg: 'from-green-500 to-green-600',
    title: '试用版',
    description: '零成本体验核心功能',
    features: [
      { label: '坐席数量', value: '1个' },
      { label: 'Token流量', value: '100,000/月' },
      { label: '文件和知识库存储', value: '1GB' },
      { label: '核心功能', value: 'AI智能客服、人工客服、知识库' },
    ],
    note: '试用期5天，试用期内用完为止',
    buttonText: '免费使用',
    buttonLink: 'https://cw.smarket.net.cn/app/login',
    noPrice: true,
  },
  {
    key: 'standard',
    icon: 'S',
    iconBg: 'from-blue-500 to-blue-700',
    iconStyle: 'background: linear-gradient(135deg, #2563EB, #013068)',
    title: '标准版',
    description: '个人及小团队的智能客服起点',
    features: [
      { label: '坐席数量', value: '1个' },
      { label: 'Token流量', value: '10,000,000/月' },
      { label: '文件和知识库存储', value: '5GB' },
      { label: '核心功能', value: 'AI智能客服、人工客服、知识库' },
    ],
    priceMonthly: '订阅 ¥95/月',
    priceYearly: '订阅 ¥1026/年',
  },
  {
    key: 'professional',
    icon: 'P',
    iconBg: 'from-blue-500 to-blue-700',
    iconStyle: 'background: linear-gradient(135deg, #2563EB, #013068)',
    title: '专业版',
    description: '高效协作，专业赋能成长型团队',
    features: [
      { label: '坐席数量', value: '3个' },
      { label: 'Token流量', value: '30,000,000/月' },
      { label: '文件和知识库存储', value: '10GB' },
      { label: '核心功能', value: '包含基础版所有功能 + 高级分析' },
      { label: '专属客服', value: '7×24小时技术支持' },
    ],
    priceMonthly: '订阅 ¥280/月',
    priceYearly: '订阅 ¥3024/年',
  },
  {
    key: 'enterprise',
    icon: 'E',
    iconBg: 'from-blue-500 to-blue-700',
    iconStyle: 'background: linear-gradient(135deg, #2563EB, #013068)',
    title: '企业版',
    description: '专属定制，满足大型业务需求',
    features: [
      { label: '坐席数量', value: '定制' },
      { label: 'Token流量', value: '定制' },
      { label: '文件和知识库存储', value: '定制' },
      { label: '核心功能', value: '包含专业版所有功能 + 定制化服务' },
      { label: '专属客服', value: '7×24小时技术支持 + 客户成功经理' },
    ],
    note: '根据业务或者团队需求定制',
    buttonText: '联系销售',
    buttonLink: '#',
    noPrice: true,
  },
];
</script>

<template>
  <div class="flex flex-col gap-8 py-6">
    <!-- 当前套餐状态卡片（顶部） -->
    <div class="plan-hero rounded-2xl overflow-hidden relative">
      <div class="px-8 py-8 flex items-center gap-8">
        <!-- 左侧：套餐信息 -->
        <div class="flex-1 min-w-0 flex flex-col gap-12">
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-3">
              <span class="font-bold text-slate-900 dark:text-white tracking-tight" style="font-size:32px">{{ displayPlanName }}</span>
              <span
                class="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white text-black"
              >
                <i class="i-ph-sparkle-fill text-xs" />
                {{ planStatus.label }}
              </span>
            </div>
            <p v-if="planLabel" class="text-sm !mb-0">
              <span class="plan-label-gradient font-semibold">{{ planLabel }}</span>
              <span class="text-slate-900 dark:text-slate-200 mx-1">·</span>
              <span class="text-slate-900 dark:text-slate-200">{{ planSlogan }}</span>
            </p>
          </div>
          <div class="flex flex-col gap-4">
            <p class="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400 !mb-0">
              <i class="i-lucide-calendar-range size-3.5" />
              有效期至{{ planExpiresAt || '未设置' }}
            </p>
            <button
              class="self-start px-5 py-2.5 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium hover:opacity-90 transition-opacity"
              @click="$refs.planCards.scrollIntoView({ behavior: 'smooth', block: 'start' })"
            >
              变更订阅
            </button>
          </div>
        </div>

        <!-- 右侧：装饰图 -->
        <img
          :src="'/dashboard/images/subscription/plan-hero.png'"
          alt="plan"
          class="hidden lg:block absolute right-0 top-0 bottom-0 h-full w-auto"
        />
      </div>
    </div>

    <!-- 标题区 -->
    <div class="text-center">
      <h2 class="font-bold text-n-slate-12" style="font-size:40px">订阅套餐和价格</h2>

      <!-- Tab 切换 -->
      <div
        class="mt-5 inline-flex rounded-xl p-1"
        style="background-color: #f1f5f9"
      >
        <button
          :class="[
            'px-9 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300',
            activeTab === 'monthly'
              ? 'bg-white text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12',
          ]"
          @click="activeTab = 'monthly'"
        >
          月付
        </button>
        <div class="relative">
          <button
            :class="[
              'px-9 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300',
              activeTab === 'yearly'
                ? 'bg-white text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12',
            ]"
            @click="activeTab = 'yearly'"
          >
            年付
          </button>
          <span
            class="absolute -top-2.5 -right-3 bg-gradient-to-b from-red-500 to-red-600 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-md leading-snug whitespace-nowrap rotate-[12deg] animate-badge-float"
          >
            9折
          </span>
        </div>
      </div>
    </div>

    <!-- 套餐卡片 -->
    <div
      ref="planCards"
      class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5"
    >
      <div
        v-for="plan in plans"
        :key="plan.key"
        :class="[
          'plan-card bg-white dark:bg-slate-800 rounded-2xl p-6 border-2 shadow-sm hover:shadow-lg hover:-translate-y-2 transition-all duration-300 flex flex-col relative',
          plan.key === 'standard' ? 'shadow-lg' : 'border-transparent',
        ]"
        :style="plan.key === 'standard' ? { borderColor: '#2563EB' } : {}"
      >
        <!-- 最受欢迎标签 -->
        <span
          v-if="plan.key === 'standard'"
          class="absolute -top-2.5 right-4 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm"
          style="background: linear-gradient(135deg, #f97316, #ef4444)"
        >
          🔥 最受欢迎
        </span>
        <!-- 标题 -->
        <h4 class="font-bold text-n-slate-12" style="font-size:24px">
          {{ plan.title }}
        </h4>
        <p class="text-n-slate-11 !mb-0" style="font-size:14px">{{ plan.description }}</p>
        <div class="mt-1 mb-5 flex items-baseline">
          <template v-if="plan.key === 'trial'">
            <span class="text-n-slate-12" style="font-size:40px">¥0</span>
            <span class="font-bold text-n-slate-11" style="font-size:16px">/月</span>
          </template>
          <template v-else-if="plan.key === 'enterprise'">
            <span class="font-extrabold text-n-slate-12" style="font-size:40px">联系销售</span>
          </template>
          <template v-else-if="activeTab === 'monthly'">
            <span class="text-n-slate-12" style="font-size:40px">{{ parsePrice(plan.priceMonthly)?.symbol }}{{ parsePrice(plan.priceMonthly)?.amount }}</span>
            <span class="font-bold text-n-slate-11" style="font-size:16px">{{ parsePrice(plan.priceMonthly)?.unit }}</span>
          </template>
          <template v-else>
            <span class="text-n-slate-12" style="font-size:40px">{{ parsePrice(plan.priceYearly)?.symbol }}{{ parsePrice(plan.priceYearly)?.amount }}</span>
            <span class="font-bold text-n-slate-11" style="font-size:16px">{{ parsePrice(plan.priceYearly)?.unit }}</span>
          </template>
        </div>

        <!-- 按钮 -->
        <div class="mb-5">
          <!-- 试用版：当前是试用版时显示"当前套餐"，标准版/专业版时禁用，其他显示外部链接 -->
          <template v-if="plan.key === 'trial'">
            <!-- 当前是试用版 -->
            <div
              v-if="currentPlanName === 'trial'"
              class="text-center rounded-lg py-2.5 text-sm font-semibold bg-green-100 text-green-700 border-2 border-green-300"
            >
              ✓ 当前套餐
            </div>
            <!-- 当前是标准版或专业版，禁用试用版 -->
            <button
              v-else-if="!getPurchaseStatus(plan.key).allowed"
              type="button"
              class="block w-full text-center rounded-lg py-2.5 text-sm font-semibold text-slate-400 bg-slate-100 cursor-not-allowed"
              disabled
            >
              {{ getPurchaseStatus(plan.key).reason }}
            </button>
            <!-- 其他情况：显示外部链接 -->
            <a
              v-else
              :href="plan.buttonLink"
              class="plan-btn block text-center rounded-lg text-white py-2.5 text-sm font-semibold transition-colors"
            >
              {{ plan.buttonText }}
            </a>
          </template>
          <!-- 企业版：点击弹出二维码 -->
          <button
            v-else-if="plan.key === 'enterprise'"
            type="button"
            class="plan-btn block text-center rounded-lg text-white py-2.5 text-sm font-semibold transition-colors w-full"
            @click="openQRModal"
          >
            {{ plan.buttonText }}
          </button>
          <!-- 标准版/专业版：触发购买弹窗 -->
          <template v-else>
            <button
              v-show="activeTab === 'monthly'"
              type="button"
              :class="[
                'block w-full text-center rounded-lg py-2.5 text-sm font-semibold transition-colors',
                getPurchaseStatus(plan.key).allowed
                  ? 'text-white cursor-pointer plan-btn'
                  : 'text-slate-400 bg-slate-100 cursor-not-allowed',
              ]"
              :disabled="!getPurchaseStatus(plan.key).allowed"
              @click="getPurchaseStatus(plan.key).allowed && openBuyModal(plan.key)"
            >
              {{ getPurchaseStatus(plan.key).allowed ? '特惠订阅' : getPurchaseStatus(plan.key).reason }}
            </button>
            <button
              v-show="activeTab === 'yearly'"
              type="button"
              :class="[
                'block w-full text-center rounded-lg py-2.5 text-sm font-semibold transition-colors',
                getPurchaseStatus(plan.key).allowed
                  ? 'text-white cursor-pointer plan-btn'
                  : 'text-slate-400 bg-slate-100 cursor-not-allowed',
              ]"
              :disabled="!getPurchaseStatus(plan.key).allowed"
              @click="getPurchaseStatus(plan.key).allowed && openBuyModal(plan.key)"
            >
              {{ getPurchaseStatus(plan.key).allowed ? '特惠订阅' : getPurchaseStatus(plan.key).reason }}
            </button>
          </template>
        </div>

        <!-- 功能列表 -->
        <ul class="space-y-3 flex-1">
          <li
            v-for="feature in plan.features"
            :key="feature.label"
            class="flex items-start text-sm text-n-slate-12"
          >
            <span class="text-slate-900 mr-3 mt-0.5 flex-shrink-0 text-sm">
              ✓
            </span>
            <span>
              <strong class="font-normal">{{ feature.label }}</strong> -
              {{ feature.value }}
            </span>
          </li>
        </ul>

        <!-- 备注 -->
        <p
          v-if="plan.note"
          class="text-xs text-n-slate-11 mt-4 leading-relaxed"
        >
          {{ plan.note }}
        </p>
      </div>
    </div>
  </div>

  <!-- 购买套餐弹窗 -->
  <BuyPlanModal
    ref="buyPlanModalRef"
    :plan-key="selectedPlan"
    :billing-cycle="selectedBillingCycle"
  />

  <!-- 联系销售二维码弹窗 -->
  <woot-modal
    v-model:show="showQRModal"
    :on-close="closeQRModal"
    size="small"
    :header-title="'联系销售'"
  >
    <div class="flex flex-col items-center gap-4 py-4">
      <img
        :src="qrCodeImage"
        alt="联系销售二维码"
        class="w-64 h-64 object-contain rounded-lg"
      />
      <p class="text-sm text-slate-600 dark:text-slate-400 text-center">
        扫描二维码，联系销售获取企业版定制方案
      </p>
    </div>
  </woot-modal>
</template>

<style scoped>
@keyframes badge-float {
  0%,
  100% {
    transform: rotate(12deg) translateY(0);
  }
  50% {
    transform: rotate(12deg) translateY(-5px);
  }
}

.animate-badge-float {
  animation: badge-float 2s ease-in-out infinite;
}

.plan-card:hover {
  border-color: #2563EB;
}

.plan-btn {
  background-color: #2563EB;
}

.plan-btn:hover {
  background-color: #1d4ed8;
}

.plan-hero {
  background: linear-gradient(135deg, #E8F0FE 0%, #DBEAFE 50%, #D1E6FF 100%);
}

.dark .plan-hero {
  background: linear-gradient(135deg, #1e293b 0%, #1e3a5f 50%, #1a2744 100%);
}

.plan-label-gradient {
  background: linear-gradient(135deg, #2563EB, #7C3AED);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
</style>
